<script setup lang="ts">
import { useRouter } from 'vue-router'

import integrationCheck from '../assets/integration_check.png'
import scheduleCheck from '../assets/schedule_check.png'
import type { SummaryEmptyState } from '../models/panel.types'

import { CONNECTIONS_ROUTE } from '@/features/integrations'
import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui'

defineProps<{ state: Exclude<SummaryEmptyState, null> }>()
const { t } = useI18n()
const router = useRouter()

// `{ name: 'WorkspaceSettings', query: { selected_tab: 'Connections' } }` landed the user on
// Workspace Basics: `WorkspaceSettings` names the `/settings` INDEX child and `SettingsIndex`
// replaces straight to `firstVisibleEntry`, dropping the query on the way. Repointed at 12's real
// pane (ORP-19897) — which leaves v4 with NO `?selected_tab` producer anywhere.
//
// A shim DOES read the param now (ORP-19899, `app/shell/registerSettingsQueryCallbacks.ts`), but it
// exists for externally-held legacy URLs — bookmarks, mails, support macros — not for this CTA, and
// v3 itself dropped `?selected_tab` for path-based deep links (ORP-19133). Push the pane's route
// name here; do not reintroduce the query param on the strength of the shim existing.
function connect(): void {
  void router.push(CONNECTIONS_ROUTE)
}
</script>

<template>
  <section
    class="summary-empty flex flex-col justify-center rounded-2xl bg-surface"
    :class="[
      `summary-empty--${state}`,
      state === 'schedules' ? 'min-h-0' : 'min-h-[calc(100dvh-11rem)]',
    ]"
  >
    <div
      class="summary-empty__body flex flex-col items-center gap-xl p-lg text-center"
      :class="{ 'py-md pb-xl': state === 'schedules' }"
    >
      <img
        :src="state === 'integrations' ? integrationCheck : scheduleCheck"
        :alt="t(`summary.empty.${state}.imageAlt`)"
        class="summary-empty__image h-auto w-full object-contain"
        :class="[
          state === 'integrations' ? 'mt-sm lg:w-[57.142857%]' : '',
          state === 'schedules' ? 'md:max-lg:w-1/2 lg:w-1/3' : '',
        ]"
      />
      <h1
        class="summary-empty__title m-0 font-bold leading-tight text-primary"
        :class="state === 'schedules' ? 'text-3xl' : 'text-2xl'"
      >
        <template v-if="state === 'integrations'">
          <span
            class="summary-empty__gradient bg-[image:var(--ui-gradient-brand)] bg-clip-text text-transparent"
            >{{ t('summary.empty.integrations.emphasis') }}</span
          >
          {{ t('summary.empty.integrations.title') }}
        </template>
        <template v-else>
          {{ t('summary.empty.schedules.titleStart') }}
          <span
            class="summary-empty__gradient bg-[image:var(--ui-gradient-brand)] bg-clip-text text-transparent"
            >{{ t('summary.empty.schedules.emphasis') }}</span
          >
          {{ t('summary.empty.schedules.titleEnd') }}
        </template>
      </h1>
      <p
        class="summary-empty__description m-0 font-semibold text-muted"
        :class="state === 'schedules' ? 'mb-xs text-sm' : 'text-base'"
      >
        {{ t(`summary.empty.${state}.description`) }}
      </p>
      <Button v-if="state === 'integrations'" @click="connect">
        {{ t('summary.empty.integrations.action') }}
      </Button>
    </div>
  </section>
</template>
